import type {
  EntrarACondominio,
  IniciarSesion,
  RespuestaSesion,
  Salud,
  UsuarioSesion,
} from '@kodica/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { almacenToken, pedir } from './api';
import { ESPERA_PERFIL_MS, reaccionAlFallo } from './reanudar';
import { olvidarRegistroDeAvisos, registrarParaAvisos } from './avisos';

interface Sesion {
  usuario: UsuarioSesion | null;
  cargando: boolean;
  /**
   * Hay sesión guardada pero no se pudo confirmar por falta de red.
   *
   * **No es lo mismo que no tener sesión**, y la app tiene que enseñar cosas
   * distintas: sin sesión se pide entrar; sin conexión se dice que no hay
   * conexión. Antes las dos acababan en el formulario de login, así que una
   * caída del servidor se veía igual que «te sacamos» — y el usuario tecleaba
   * su contraseña para recibir un error que no explicaba nada.
   */
  sinConexion: boolean;
  /** Vuelve a intentar confirmar la sesión. Lo llama el botón de reintentar. */
  reintentar: () => void;
  /**
   * Qué es el servidor con el que se está hablando.
   *
   * No es una preferencia de la app: en la nube **no existen** las rutas de
   * caseta, telefonía ni lecturas de tag, así que ofrecerlas en el menú lleva a
   * un 404 con la persona ya dentro. Se pregunta en vez de compilarse para que
   * la misma imagen sirva en los dos lados.
   *
   * Arranca en `caseta`, que es lo que ha sido siempre: si la consulta falla o
   * todavía no volvió, el menú es el de antes y no desaparece nada.
   */
  rolServidor: 'caseta' | 'nube';
  iniciar: (datos: IniciarSesion) => Promise<void>;
  cerrar: () => void;
  /**
   * La persona acaba de poner su propia contraseña.
   *
   * Se apaga aquí y no se vuelve a preguntar al API: el token sigue siendo
   * válido —no depende de la contraseña— así que no hay nada que refrescar, y
   * pedir el perfil otra vez solo añadiría una espera entre «guardado» y poder
   * salir de la pantalla que obliga a cambiarla.
   */
  marcarPasswordCambiada: () => void;

  /**
   * Entrar a un fraccionamiento: lo pone activo para esta sesión.
   *
   * Devuelve un **token nuevo**, y por eso pasa por aquí y no por la pantalla
   * que lo pide: el condominio activo viaja dentro del token, así que cambiarlo
   * es cambiar la credencial de la sesión entera. Guardarlo en un sitio y el
   * usuario en otro es cómo se quedan desalineados.
   */
  entrarACondominio: (condominioId: string) => Promise<void>;
}

const ContextoSesion = createContext<Sesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [sinConexion, setSinConexion] = useState(false);
  const [rolServidor, setRolServidor] = useState<'caseta' | 'nube'>('caseta');

  // Una sola vez y sin sesión: `/salud` es público, y el rol hace falta antes
  // de pintar el primer menú.
  useEffect(() => {
    pedir<Salud>('/salud')
      .then((s) => setRolServidor(s.rol === 'nube' ? 'nube' : 'caseta'))
      // Un servidor viejo no manda `rol`, y una caseta sin red tampoco
      // contesta. En los dos casos se queda en `caseta`, que es lo que era.
      .catch(() => undefined);
  }, []);

  /**
   * Al recargar la pestaña hay un token pero no un usuario. Se revalida contra
   * el API en vez de confiar en lo que haya en localStorage: si al guardia lo
   * dieron de baja, la pantalla no debe volver a abrirse.
   *
   * ## Un fallo de red no es una sesión inválida
   *
   * Antes, **cualquier** error borraba el token. En un navegador casi no se
   * nota; en el teléfono sí, porque el WebView arranca antes de que la conexión
   * se asiente y esta es la primera petición que sale. Bastaba abrir la app con
   * la red todavía despertando para quedarse sin sesión — y como el token ya no
   * estaba, no había forma de recuperarla salvo volver a teclear la contraseña.
   * Cada vez.
   *
   * Ahora solo la mata el 401, que es el servidor diciendo que quien pregunta
   * ya no es nadie —y ahí el token lo borra `pedir` sola—. Lo demás se
   * reintenta unas veces, y si aun así no hay red **el token se conserva**:
   * puede que esta vez toque volver a entrar, pero la próxima que haya señal la
   * sesión sigue en pie.
   */
  const revalidar = useCallback(async () => {
    if (!almacenToken.leer()) {
      setSinConexion(false);
      setCargando(false);
      return;
    }

    setCargando(true);
    setSinConexion(false);

    for (let intento = 1; ; intento++) {
      try {
        setUsuario(await pedir<UsuarioSesion>('/auth/perfil'));
        break;
      } catch (e) {
        const reaccion = reaccionAlFallo(e, intento);
        // La sesión murió de verdad: `pedir` ya borró el token, y toca entrar.
        if (reaccion === 'sesion-muerta') break;
        if (reaccion === 'sin-conexion') {
          // El token **sigue guardado**. Esto no es una sesión cerrada: es una
          // sesión que no se pudo confirmar, y se enseñan cosas distintas.
          setSinConexion(true);
          break;
        }
        await new Promise((r) => setTimeout(r, intento * ESPERA_PERFIL_MS));
      }
    }

    setCargando(false);
  }, []);

  useEffect(() => {
    void revalidar();
  }, [revalidar]);

  const iniciar = useCallback(async (datos: IniciarSesion) => {
    const respuesta = await pedir<RespuestaSesion>('/auth/sesion', {
      metodo: 'POST',
      cuerpo: datos,
    });
    almacenToken.guardar(respuesta.token);
    setUsuario(respuesta.usuario);
  }, []);

  // Los avisos se registran cada vez que hay un residente en sesión, no solo
  // al iniciarla: cubre igual el login de hoy y el arranque de mañana, que es
  // cuando Firebase pudo haber cambiado el token. Dentro no hace nada si esto
  // corre en un navegador.
  //
  // Va aquí y no en `iniciar` porque al reabrir la app la sesión se revalida
  // contra `/auth/perfil` sin pasar por el login, y ese camino también tiene
  // que reenviar el token.
  useEffect(() => {
    if (usuario?.rol === 'RESIDENTE') void registrarParaAvisos();
  }, [usuario]);

  const entrarACondominio = useCallback(async (condominioId: string) => {
    const respuesta = await pedir<RespuestaSesion>('/auth/condominio', {
      metodo: 'POST',
      cuerpo: { condominioId } satisfies EntrarACondominio,
    });
    almacenToken.guardar(respuesta.token);
    setUsuario(respuesta.usuario);
  }, []);

  const marcarPasswordCambiada = useCallback(() => {
    setUsuario((u) => (u ? { ...u, debeCambiarPassword: false } : u));
  }, []);

  const cerrar = useCallback(() => {
    almacenToken.borrar();
    setUsuario(null);
    // El aparato sigue teniendo el token dado de alta en la caseta: darlo de
    // baja aquí es trabajo aparte. Esto solo permite que quien entre después
    // vuelva a registrarlo a su nombre.
    olvidarRegistroDeAvisos();
  }, []);

  const valor = useMemo<Sesion>(
    () => ({
      usuario,
      cargando,
      sinConexion,
      reintentar: () => void revalidar(),
      rolServidor,
      iniciar,
      cerrar,
      marcarPasswordCambiada,
      entrarACondominio,
    }),
    [
      usuario,
      cargando,
      sinConexion,
      revalidar,
      rolServidor,
      iniciar,
      cerrar,
      marcarPasswordCambiada,
      entrarACondominio,
    ],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}

export function useSesion(): Sesion {
  const contexto = useContext(ContextoSesion);
  if (!contexto) throw new Error('useSesion debe usarse dentro de <ProveedorSesion>');
  return contexto;
}
